import { Clause, LegalShell } from "@/components/LegalShell";

export default function Terms() {
  return (
    <LegalShell title="Terms & Conditions" updated="29 June 2026">
      <p className="text-sm text-slate-600 leading-relaxed" data-testid="terms-intro">
        Unbound is a learning platform where a student sets their own goal and AI agents build and teach a curriculum
        around it. By creating an account you agree to what follows. It is written to be readable by the people who
        actually use it — including a twelve-year-old.
      </p>

      <Clause n={1} title="Who can use Unbound">
        <p>
          Unbound is designed for learners roughly 6 to 18 years old. A guardian is welcome but never required, and no
          plan needs anyone's approval but the learner's. If you are under the age of digital consent where you live, use
          Unbound with a guardian's knowledge.
        </p>
      </Clause>

      <Clause n={2} title="Unbound is free">
        <p>
          The platform is free to use. To keep it that way, AI generation is metered: each learner has a daily and monthly
          allowance of agent generations, and the platform has an overall daily ceiling. If you reach a limit, everything
          already generated stays available and the allowance resets. We will never charge you without asking first.
        </p>
        <p>Automated scraping, bulk account creation, resale of generated material, or attempts to exhaust the shared allowance are not permitted.</p>
      </Clause>

      <Clause n={3} title="What the AI produces, and its limits">
        <p>
          Lesson material, practice questions and assessments are generated by AI models and grounded in real retrieved
          references — we do not invent citations. Even so, <span className="font-semibold">AI can be wrong</span>. Treat
          Unbound as a knowledgeable study partner, not an infallible authority.
        </p>
        <p>
          Unbound is not a school, not an accredited qualification, and not a substitute for professional medical, legal,
          financial or mental-health advice. If something looks wrong, report it — reports are read.
        </p>
      </Clause>

      <Clause n={4} title="Your account">
        <p>
          Keep your password to yourself. You are responsible for what happens under your account. We lock an account
          briefly after repeated failed sign-ins to protect it. Tell us if you think someone else has access.
        </p>
      </Clause>

      <Clause n={5} title="Your content and ours">
        <p>
          Your goal, your notes and your answers are yours. You give us permission to process them so the agents can
          teach you. Generated lesson material is licensed to you for personal study; the platform, its prompts and its
          orchestration remain ours.
        </p>
      </Clause>

      <Clause n={6} title="Acceptable use">
        <p>
          Use Unbound to learn. Do not use it to harass anyone, to generate harmful or adult content, to break the law,
          or to attempt to extract the platform's internal prompts and safety layers. The safety layer is not
          overridable, by anyone, including guardians.
        </p>
      </Clause>

      <Clause n={7} title="Guardians">
        <p>
          If a learner invites a guardian, that guardian gets a read-only view of progress and of what the learner is
          stuck on, and may add guidance for the agents. Guardians cannot block, approve or delete a learner's plan. A
          learner can remove their guardian at any time.
        </p>
      </Clause>

      <Clause n={8} title="Availability and changes">
        <p>
          This is an actively developed product. Features change, and generation can occasionally fail or take a while.
          We may update these terms; the date at the top changes when we do, and continued use means you accept the
          update.
        </p>
      </Clause>

      <Clause n={9} title="Ending your account">
        <p>
          You can stop using Unbound at any time and request deletion of your account and learning data. We may suspend
          an account that abuses the shared allowance or breaks the acceptable-use rules above.
        </p>
      </Clause>

      <Clause n={10} title="Liability">
        <p>
          Unbound is provided as-is, without warranty of academic outcomes. To the extent permitted by law, we are not
          liable for indirect or consequential loss arising from use of the platform.
        </p>
      </Clause>
    </LegalShell>
  );
}
